import { api } from '../../client';

export interface EmployeeDto {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  date_of_birth: string | null;
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  department: string;
  job_title: string;
  employment_type: string;
  employee_type: string;
  hire_date: string | null;
  status: string;
  salary_type: string;
  pay_rate: number;
  bank_name: string;
  account_number: string;
  routing_number: string;
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone: string;
  sss_number: string;
  pagibig_number: string;
  philhealth_number: string;
  tin_number: string;
  gsis_number: string;
  benefits_notes: string;
  has_login_account: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EmployeeLeaveCreditDto {
  id: string;
  type: 'trainee' | 'regular' | 'supervisor' | 'manager';
  credit_name: string;
  days: number;
  remaining_days: number;
  created_at?: string;
  updated_at?: string;
}

export const hrEmployeesApi = {
  async list(
    search?: string
  ): Promise<{ data?: EmployeeDto[]; message?: string; errors?: Record<string, string[]> }> {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return api.get(`/hr/employees${q}`) as Promise<{ data: EmployeeDto[] }>;
  },

  async get(id: string): Promise<{ data?: EmployeeDto; message?: string }> {
    return api.get(`/hr/employees/${id}`) as Promise<{ data: EmployeeDto }>;
  },

  async create(data: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    date_of_birth?: string;
    street_address?: string;
    city?: string;
    state_province?: string;
    postal_code?: string;
    department?: string;
    job_title?: string;
    employment_type?: string;
    employee_type?: string;
    hire_date?: string;
    status?: string;
    salary_type?: string;
    pay_rate?: number;
    bank_name?: string;
    account_number?: string;
    routing_number?: string;
    emergency_contact_name?: string;
    emergency_contact_relationship?: string;
    emergency_contact_phone?: string;
    sss_number?: string;
    pagibig_number?: string;
    philhealth_number?: string;
    tin_number?: string;
    gsis_number?: string;
    benefits_notes?: string;
    create_account?: boolean;
    password?: string;
  }): Promise<{ data?: EmployeeDto; message?: string; errors?: Record<string, string[]> }> {
    return api.post('/hr/employees', data) as Promise<{
      data: EmployeeDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async update(
    id: string,
    data: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      date_of_birth?: string;
      street_address?: string;
      city?: string;
      state_province?: string;
      postal_code?: string;
      department?: string;
      job_title?: string;
      employment_type?: string;
      employee_type?: string;
      hire_date?: string;
      status?: string;
      salary_type?: string;
      pay_rate?: number;
      bank_name?: string;
      account_number?: string;
      routing_number?: string;
      emergency_contact_name?: string;
      emergency_contact_relationship?: string;
      emergency_contact_phone?: string;
      sss_number?: string;
      pagibig_number?: string;
      philhealth_number?: string;
      tin_number?: string;
      gsis_number?: string;
      benefits_notes?: string;
    }
  ): Promise<{ data?: EmployeeDto; message?: string; errors?: Record<string, string[]> }> {
    return api.put(`/hr/employees/${id}`, data) as Promise<{
      data: EmployeeDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async delete(id: string): Promise<{ success?: boolean; message?: string }> {
    return api.delete(`/hr/employees/${id}`) as Promise<{ success?: boolean; message?: string }>;
  },

  async resetPassword(
    id: string,
    data: { password: string; password_confirmation: string }
  ): Promise<{ message?: string; errors?: Record<string, string[]> }> {
    return api.post(`/hr/employees/${id}/reset-password`, data) as Promise<{
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async getLeaveCredits(id: string): Promise<{ data?: EmployeeLeaveCreditDto[]; message?: string }> {
    return api.get(`/hr/employees/${id}/leave-credits`) as Promise<{ data: EmployeeLeaveCreditDto[] }>;
  },

  async getSelfLeaveCredits(): Promise<{ data?: EmployeeLeaveCreditDto[]; message?: string }> {
    return api.get('/employee/leave-credits') as Promise<{ data: EmployeeLeaveCreditDto[] }>;
  },
};
